import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useNotify } from '@/hooks/useNotify';

interface PendingInvitation {
  id: string;
  company_id: string;
  company_name: string;
  created_at: string;
  expires_at: string;
  status: string;
  source_table?: string;
}

interface DeactivatedMembership {
  id: string;
  company_id: string;
  company_name: string;
  role: string;
  deactivated_at: string;
  deactivation_reason: string;
}

interface UserInvitationStatus {
  pendingInvitations: PendingInvitation[];
  deactivatedMemberships: DeactivatedMembership[];
  isLoading: boolean;
  acceptInvitation: (invitationId: string, options?: { skipReload?: boolean }) => Promise<void>;
  refetch: () => Promise<void>;
}

export const useUserInvitationStatus = (): UserInvitationStatus => {
  const { user } = useAuth();
  const notify = useNotify();
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [deactivatedMemberships, setDeactivatedMemberships] = useState<DeactivatedMembership[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPendingInvitations = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase.rpc('get_pending_invitations_for_user');
      if (error) throw error;
      setPendingInvitations(data || []);
    } catch (error) {
      console.error('Error fetching pending invitations:', error);
    }
  };

  const fetchDeactivatedMemberships = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('company_members')
        .select(`
          id,
          company_id,
          role,
          deactivated_at,
          deactivation_reason,
          companies!inner(name)
        `)
        .eq('user_id', user.id)
        .eq('status', 'deactivated');

      if (error) throw error;

      const deactivatedData = (data || []).map(item => ({
        id: item.id,
        company_id: item.company_id,
        company_name: (item.companies as any)?.name || 'Unknown Company',
        role: item.role,
        deactivated_at: item.deactivated_at,
        deactivation_reason: item.deactivation_reason
      }));

      setDeactivatedMemberships(deactivatedData);
    } catch (error) {
      console.error('Error fetching deactivated memberships:', error);
    }
  };

  const acceptInvitation = async (invitationId: string, options: { skipReload?: boolean } = {}) => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase.rpc('accept_invitation_by_email', {
        p_invitation_id: invitationId
      });

      if (error) throw error;

      if (!(data as any)?.success) {
        throw new Error((data as any)?.error || 'Failed to accept invitation');
      }

      notify.success(
        options.skipReload 
          ? 'You have successfully joined the company!'
          : 'You have successfully joined the company. Redirecting...'
      );

      if (options.skipReload) {
        // Just refresh the pending invitations to reflect the change
        await refetch();
      } else {
        // Default behavior - refresh to update user's company status
        setTimeout(() => window.location.reload(), 1500);
      }
    } catch (error: any) {
      notify.error(error.message || 'Failed to accept invitation');
    } finally {
      setIsLoading(false);
    }
  };

  const refetch = async () => {
    setIsLoading(true);
    await Promise.all([fetchPendingInvitations(), fetchDeactivatedMemberships()]);
    setIsLoading(false);
  };

  useEffect(() => {
    if (user) {
      refetch();
    }
  }, [user]);

  return {
    pendingInvitations,
    deactivatedMemberships,
    isLoading,
    acceptInvitation,
    refetch
  };
};
